import React, { useState } from "react";

/* =======================
   🔢 SCORING LOGIC
   (UNCHANGED – SAME AS BEFORE)
   ======================= */
// ⬇️ PASTE YOUR FULL SCORING LOGIC HERE ⬇️
/*************************************************
 * CONFIGURATION — CHANGE VALUES ONLY
 *************************************************/

/* ---------- LANDLORD OVERLAY ---------- */
const LANDLORD_OVERLAY = {
  multiplier: 5,
  caps: {
    effortRate: 10,
    tenure: 10,
    employmentPredictability: 10,
    employerStability: 10,
    sectorCyclicality: 5,
  },
  applicable: [
    "effortRate",
    "tenure",
    "employmentPredictability",
    "employerStability",
    "sectorCyclicality",
  ],
};

/* ---------- DECISION THRESHOLDS ---------- */
const DECISION_THRESHOLDS = {
  Conservative: { approve: 85, review: 70 },
  Balanced: { approve: 80, review: 65 },
  Aggressive: { approve: 75, review: 60 },
};

/*************************************************
 * SUB-SCORE FUNCTIONS
 *************************************************/

function effortRateScore(v) {
  if (v < 20) return 100;
  if (v < 30) return 80;
  if (v < 35) return 65;
  if (v < 40) return 45;
  return 20;
}

function tenureScore(m) {
  if (m >= 36) return 100;
  if (m >= 12) return 80;
  if (m >= 6) return 60;
  if (m >= 3) return 40;
  return 20;
}

function employmentPredictabilityScore(t) {
  return {
    PUBLIC_PERMANENT: 100,
    PRIVATE_PERMANENT: 90,
    FIXED_TERM_LONG: 85,
    FIXED_TERM_SHORT: 65,
    SELF_EMPLOYED_OLD: 70,
    SELF_EMPLOYED_GIG: 55,
    PLATFORM_GIG: 45,
    STUDENT: 20,
    UNEMPLOYED: 0,
  }[t];
}

function employerStabilityScore(t) {
  return {
    PUBLIC: 100,
    MULTINATIONAL: 95,
    LARGE_NATIONAL: 85,
    ESTABLISHED_SME: 70,
    SMALL_BUSINESS: 55,
    STARTUP: 40,
    UNKNOWN: 30,
  }[t];
}

function sectorCyclicalityScore(t) {
  return {
    PUBLIC_SECTOR: 100,
    UTILITIES: 95,
    HEALTHCARE: 95,
    EDUCATION: 90,
    CORE_TECH: 85,
    FINANCE: 80,
    ENERGY: 80,
    MANUFACTURING_NON_CYCLICAL: 80,
    LOGISTICS: 75,
    MANUFACTURING_CYCLICAL: 70,
    PROFESSIONAL_SERVICES: 70,
    WHOLESALE: 70,
    RETAIL: 60,
    COMMERCE: 60,
    ENTERTAINMENT: 55,
    HOSPITALITY: 50,
    TOURISM: 50,
    CONSTRUCTION: 45,
    GIG_ECONOMY: 40,
    FREELANCE: 40,
  }[t];
}

function incomeConsistencyScore(v) {
  if (v.toString().trim() === "" || v === 0) return 60;
  if (v < 10) return 100;
  if (v <= 25) return 70;
  if (v > 25) return 40;
  return 20;
}

function incomeVerificationScore(src, verified) {
  const base = {
    TAX: 90,
    LOCAL_PAYSLIP: 85,
    FOREIGN_PAYSLIP: 80,
    BANK_ONLY: 75,
    SELF_DECLARED: 60,
  }[src];
  return verified ? base + 10 : base;
}

function financialSkorScore(v) {
  if (v >= 750) return 100;
  if (v >= 650) return 85;
  if (v >= 550) return 70;
  if (v >= 450) return 50;
  if (v >= 250 || v === 0 || v.toString().trim() == "") return 40;
  return 30;
}

function bankBehaviourScore(o, b, v) {
  return o * 0.4 + b * 0.35 + v * 0.25;
}

function rentBehaviourScore(m) {
  if (m >= 12) return 100;
  if (m >= 6) return 85;
  if (m >= 3) return 70;
  return 60;
}

function defaultHistoryScore(s) {
  if (s === "ACTIVE") return { hardFail: true };
  if (s === "OLD") return 70;
  if (s === "RECENT") return 40;
  return 100;
}

function insuranceScore(s) {
  if (s === "INSURED") return 100;
  if (s === "ELIGIBLE") return 70;
  if (s === "NOT_ELIGIBLE") return { hardFail: true };
}

function documentationScore(missing, critical) {
  if (critical) return { hardFail: true };
  if (missing === 0) return 100;
  if (missing === 1) return 70;
  return 40;
}

function forensicsScore(f) {
  return {
    TRUSTED: 100,
    NORMAL: 85,
    WARNING: 60,
    HIGH_RISK: 10,
    MISSING: 40,
  }[f];
}

// ⬆️ DO NOT MODIFY ⬆️

/* =======================
   🧾 DROPDOWN OPTIONS
   ======================= */

const POLICY_PRESETS = ["Conservative", "Balanced", "Aggressive"];

const INCOME_SOURCES = {
  TAX: "Tax Authority",
  LOCAL_PAYSLIP: "Local Payslip",
  FOREIGN_PAYSLIP: "Foreign Payslip",
  BANK_ONLY: "Bank Only",
  SELF_DECLARED: "Self Declared",
};

const EMPLOYMENT_TYPES = {
  PUBLIC_PERMANENT: "Public Servant (Permanent)",
  PRIVATE_PERMANENT: "Private Employee (Permanent)",
  FIXED_TERM_LONG: "Fixed-term >12 months",
  FIXED_TERM_SHORT: "Fixed-term <12 months",
  SELF_EMPLOYED_OLD: "Self-employed (>2 years)",
  SELF_EMPLOYED_GIG: "Self-employed / Gig",
  PLATFORM_GIG: "Platform Gig Work",
  STUDENT: "Student / No Income",
  UNEMPLOYED: "Unemployed",
};

const EMPLOYER_TYPES = {
  PUBLIC: "Government / Public Sector",
  MULTINATIONAL: "Multinational / Blue-chip",
  LARGE_NATIONAL: "Large National Company",
  ESTABLISHED_SME: "Established SME",
  SMALL_BUSINESS: "Small Business / Young SME",
  STARTUP: "Startup / Pre-profit",
  UNKNOWN: "Unknown / Unverifiable",
};

const SECTORS = {
  PUBLIC_SECTOR: "Public Sector / Government",
  UTILITIES: "Utilities",
  HEALTHCARE: "Healthcare",
  EDUCATION: "Education",
  CORE_TECH: "Core Tech / IT Services",
  FINANCE: "Finance / Insurance",
  ENERGY: "Energy (Non-speculative)",
  MANUFACTURING_NON_CYCLICAL: "Manufacturing (Non-cyclical)",
  LOGISTICS: "Logistics / Transport",
  MANUFACTURING_CYCLICAL: "Manufacturing (Cyclical)",
  PROFESSIONAL_SERVICES: "Professional Services",
  WHOLESALE: "Wholesale / Distribution",
  RETAIL: "Retail (Non-essential)",
  COMMERCE: "Commerce / Sales",
  ENTERTAINMENT: "Entertainment / Events",
  HOSPITALITY: "Hospitality / Restaurants",
  TOURISM: "Tourism",
  CONSTRUCTION: "Construction",
  GIG_ECONOMY: "Gig Economy",
  FREELANCE: "Freelance / Creative",
};

const DEFAULT_HISTORY = {
  NONE: "No Defaults Ever",
  OLD: "Defaults >3 Years Ago (Resolved)",
  RECENT: "Defaults <3 Years Ago (Resolved)",
  ACTIVE: "Active Defaults (Hard Fail)",
};

const INSURANCE_STATUS = {
  INSURED: "Insured by Skor",
  ELIGIBLE: "Eligible but Not Taken",
  NOT_ELIGIBLE: "Not Eligible (Hard Fail)",
};

const FORENSICS_FLAGS = {
  TRUSTED: "Trusted",
  NORMAL: "Normal",
  WARNING: "Warning",
  HIGH_RISK: "High Risk",
  MISSING: "Missing",
};

/* =======================
      🖥️ REACT PAGE
      ======================= */

export default function ScoringPage() {
  /* ---------- PILLAR WEIGHTS (MUST SUM TO 1) ---------- */
  const [PILLARS, setPILLARS] = useState({
    incomeAffordability: {
      effortRate: 0.5,
      incomeVerification: 0.3,
      incomeConsistency: 0.2,
    },
    employmentStability: {
      employmentPredictability: 0.3,
      tenure: 0.3,
      employerStability: 0.25,
      sectorCyclicality: 0.15,
    },
    financialBehaviour: {
      financialSkor: 0.35,
      bankBehaviour: 0.3,
      rentBehaviour: 0.35,
    },
    creditDefaultRisk: {
      defaultHistory: 0.6,
      insuranceSkor: 0.4,
    },
    documentationStrength: {
      documentation: 0.8,
      forensics: 0.2,
    },
  });

  /* ---------- POLICY WEIGHTS ---------- */
  const [POLICY_WEIGHTS, setPOLICY_WEIGHTS] = useState({
    incomeAffordability: 0.2,
    employmentStability: 0.2,
    financialBehaviour: 0.2,
    creditDefaultRisk: 0.2,
    documentationStrength: 0.2,
  });
  const [input, setInput] = useState({
    policyPreset: "Balanced",

    effortRate: 28,
    incomeSource: "LOCAL_PAYSLIP",
    incomeVerified: true,
    incomeVariance: 12,

    employmentType: "PRIVATE_PERMANENT",
    tenureMonths: 18,
    employerType: "ESTABLISHED_SME",
    sector: "CORE_TECH",

    financialSkor: 720,
    // overdraftScore: 80,
    // balanceVsRentScore: 100,
    // volatilityScore: 70,
    bankBehaviourScore: 10,

    rentHistoryMonths: 6,

    defaultHistory: "NONE",
    insuranceStatus: "ELIGIBLE",

    missingDocs: 1,
    criticalDocMissing: false,
    forensicsFlag: "NORMAL",

    landlordPoints: {
      effortRate: 0,
      tenure: 0,
      employmentPredictability: 0,
      employerStability: 0,
      sectorCyclicality: 0,
    },
  });

  const [result, setResult] = useState(null);

  const update = (k, v) => setInput((p) => ({ ...p, [k]: v }));
  const updateOverlay = (k, v) =>
    setInput((p) => ({
      ...p,
      landlordPoints: { ...p.landlordPoints, [k]: v },
    }));

  const updatePolicyWeight = (key, value) => {
    setPOLICY_WEIGHTS((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updatePillar = (pillar, key, value) => {
    setPILLARS((prev) => ({
      ...prev,
      [pillar]: {
        ...prev[pillar],
        [key]: value,
      },
    }));
  };

  /*************************************************
   * MAIN FUNCTION
   *************************************************/

  function calculateFinalDecision(input) {
    const scores = {};

    scores.effortRate = effortRateScore(input.effortRate);
    scores.tenure = tenureScore(input.tenureMonths);
    scores.employmentPredictability = employmentPredictabilityScore(
      input.employmentType
    );
    scores.employerStability = employerStabilityScore(input.employerType);
    scores.sectorCyclicality = sectorCyclicalityScore(input.sector);

    scores.incomeConsistency = incomeConsistencyScore(input.incomeVariance);
    scores.incomeVerification = incomeVerificationScore(
      input.incomeSource,
      input.incomeVerified
    );

    scores.financialSkor = financialSkorScore(input.financialSkor);
    scores.bankBehaviour = input.bankBehaviour;
    //   scores.bankBehaviour = bankBehaviourScore(
    //     input.overdraftScore,
    //     input.balanceVsRentScore,
    //     input.volatilityScore
    //   );
    scores.rentBehaviour = rentBehaviourScore(input.rentHistoryMonths);

    const def = defaultHistoryScore(input.defaultHistory);
    if (def?.hardFail) return { status: "FAILED", reason: "Active Default" };
    scores.defaultHistory = def;

    const ins = insuranceScore(input.insuranceStatus);
    if (ins?.hardFail)
      return { status: "FAILED", reason: "Insurance Hard Fail" };
    scores.insuranceSkor = ins;

    const doc = documentationScore(input.missingDocs, input.criticalDocMissing);
    if (doc?.hardFail)
      return { status: "FAILED", reason: "Documentation Hard Fail" };
    scores.documentation = doc;

    scores.forensics = forensicsScore(input.forensicsFlag);

    /* Apply Landlord Overlay */
    for (const k of LANDLORD_OVERLAY.applicable) {
      const adj = Math.max(
        -LANDLORD_OVERLAY.caps[k],
        Math.min(
          LANDLORD_OVERLAY.caps[k],
          (input.landlordPoints?.[k] || 0) * LANDLORD_OVERLAY.multiplier
        )
      );
      scores[k] += adj;
    }

    /* Pillar Scores */
    const pillarScores = {};
    for (const p in PILLARS) {
      pillarScores[p] = Object.entries(PILLARS[p]).reduce(
        (s, [k, w]) => s + scores[k] * w,
        0
      );
    }

    /* Final Score */
    const finalScore = Object.entries(POLICY_WEIGHTS).reduce(
      (s, [p, w]) => s + pillarScores[p] * w,
      0
    );

    /* Decision */
    const t = DECISION_THRESHOLDS[input.policyPreset];
    const decision =
      finalScore >= t.approve
        ? "APPROVE"
        : finalScore >= t.review
          ? "REVIEW"
          : "REJECT";

    return {
      status: "SUCCESS",
      finalScore: finalScore.toFixed(2),
      pillarScores,
      decision,
    };
  }

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      <h2>Tenant Scoring Engine</h2>
      {/* POLICY */}
      <h3>Policy</h3>
      <label>Risk Policy</label>
      <select
        value={input.policyPreset}
        onChange={(e) => update("policyPreset", e.target.value)}
      >
        {POLICY_PRESETS.map((p) => (
          <option key={p}>{p}</option>
        ))}
      </select>
      {/* INCOME */}
      <h3>Income & Affordability</h3>
      <label>Effort Rate (%)</label>
      <input
        type="number"
        value={input.effortRate}
        onChange={(e) => update("effortRate", e.target.value)}
      />
      <label>Income Source</label>
      <select
        value={input.incomeSource}
        onChange={(e) => update("incomeSource", e.target.value)}
      >
        {Object.entries(INCOME_SOURCES).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      <label>
        <input
          type="checkbox"
          checked={input.incomeVerified}
          onChange={(e) => update("incomeVerified", e.target.checked)}
        />
        Income Verified
      </label>
      <label>Income Variance (%)</label>
      <input
        type="number"
        value={input.incomeVariance}
        onChange={(e) => update("incomeVariance", e.target.value)}
      />
      {/* EMPLOYMENT */}
      <h3>Employment Stability</h3>
      <label>Employment Type</label>
      <select
        value={input.employmentType}
        onChange={(e) => update("employmentType", e.target.value)}
      >
        {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      <label>Tenure (months)</label>
      <input
        type="number"
        value={input.tenureMonths}
        onChange={(e) => update("tenureMonths", e.target.value)}
      />
      <label>Employer Type</label>
      <select
        value={input.employerType}
        onChange={(e) => update("employerType", e.target.value)}
      >
        {Object.entries(EMPLOYER_TYPES).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      <label>Industry / Sector</label>
      <select
        value={input.sector}
        onChange={(e) => update("sector", e.target.value)}
      >
        {Object.entries(SECTORS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {/* FINANCIAL */}
      <h3>Financial Behaviour</h3>
      <label>Financial Skor</label>
      <input
        type="number"
        value={input.financialSkor}
        onChange={(e) => update("financialSkor", e.target.value)}
      />
      {/* <label>Overdraft Behaviour Score</label>
      <input
        type="number"
        value={input.overdraftScore}
        onChange={(e) => update("overdraftScore", e.target.value)}
      />

      <label>Avg Balance vs Rent Score</label>
      <input
        type="number"
        value={input.balanceVsRentScore}
        onChange={(e) => update("balanceVsRentScore", e.target.value)}
      />

      <label>Balance Volatility Score</label>
      <input
        type="number"
        value={input.volatilityScore}
        onChange={(e) => update("volatilityScore", e.target.value)}
    /> */}
      <label htmlFor="">
        Bank Behaviour (For now exact value that will be considered)
      </label>
      <input
        type="number"
        value={input.volatilityScore}
        onChange={(e) => update("bankBehaviour", e.target.value)}
      />
      {console.log(
        rentBehaviourScore(input.rentHistoryMonths),
        "lkasjakljdskad"
      )}
      <label>Rent Payment History (months)</label>
      <input
        type="number"
        value={input.rentHistoryMonths}
        onChange={(e) => update("rentHistoryMonths", e.target.value)}
      />
      {/* RISK */}
      <h3>Credit & Default Risk</h3>
      <label>Default History</label>
      <select
        value={input.defaultHistory}
        onChange={(e) => update("defaultHistory", e.target.value)}
      >
        {Object.entries(DEFAULT_HISTORY).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      <label>Insurance Status</label>
      <select
        value={input.insuranceStatus}
        onChange={(e) => update("insuranceStatus", e.target.value)}
      >
        {Object.entries(INSURANCE_STATUS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {/* DOCUMENTATION */}
      <h3>Documentation Strength</h3>
      <label>Missing Documents (count)</label>
      <input
        type="number"
        value={input.missingDocs}
        onChange={(e) => update("missingDocs", e.target.value)}
      />
      <label>
        <input
          type="checkbox"
          checked={input.criticalDocMissing}
          onChange={(e) => update("criticalDocMissing", e.target.checked)}
        />
        Critical Document Missing
      </label>
      <label>Forensics Flag</label>
      <select
        value={input.forensicsFlag}
        onChange={(e) => update("forensicsFlag", e.target.value)}
      >
        {Object.entries(FORENSICS_FLAGS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {/* OVERLAY */}
      <h3>Landlord Overlay</h3>
      <label>Effort Rate Adjustment</label>
      <input
        type="number"
        value={input.landlordPoints.effortRate}
        onChange={(e) => updateOverlay("effortRate", e.target.value)}
      />
      <label>Tenure Adjustment</label>
      <input
        type="number"
        value={input.landlordPoints.tenure}
        onChange={(e) => updateOverlay("tenure", e.target.value)}
      />
      <label>Employment Predictability Adjustment</label>
      <input
        type="number"
        value={input.landlordPoints.employmentPredictability}
        onChange={(e) =>
          updateOverlay("employmentPredictability", e.target.value)
        }
      />
      <label>Employer Stability Adjustment</label>
      <input
        type="number"
        value={input.landlordPoints.employerStability}
        onChange={(e) => updateOverlay("employerStability", e.target.value)}
      />
      <label>Sector Cyclicality Adjustment</label>
      <input
        type="number"
        value={input.landlordPoints.sectorCyclicality}
        onChange={(e) => updateOverlay("sectorCyclicality", e.target.value)}
      />
      <hr />
      <hr />
      <h3>Pillars %</h3>

      <h6>incomeAffordability</h6>
      <label>effortRate:</label>
      <input
        type="number"
        value={PILLARS.incomeAffordability.effortRate}
        onChange={(e) =>
          updatePillar("incomeAffordability", "effortRate", e.target.value)
        }
      />

      <label>incomeVerification:</label>
      <input
        type="number"
        value={PILLARS.incomeAffordability.incomeVerification}
        onChange={(e) =>
          updatePillar(
            "incomeAffordability",
            "incomeVerification",
            e.target.value
          )
        }
      />

      <label>incomeConsistency:</label>
      <input
        type="number"
        value={PILLARS.incomeAffordability.incomeConsistency}
        onChange={(e) =>
          updatePillar(
            "incomeAffordability",
            "incomeConsistency",
            e.target.value
          )
        }
      />

      <h6>employmentStability</h6>
      <label>employmentPredictability:</label>
      <input
        type="number"
        value={PILLARS.employmentStability.employmentPredictability}
        onChange={(e) =>
          updatePillar(
            "employmentStability",
            "employmentPredictability",
            e.target.value
          )
        }
      />

      <label>tenure:</label>
      <input
        type="number"
        value={PILLARS.employmentStability.tenure}
        onChange={(e) =>
          updatePillar("employmentStability", "tenure", e.target.value)
        }
      />

      <label>employerStability:</label>
      <input
        type="number"
        value={PILLARS.employmentStability.employerStability}
        onChange={(e) =>
          updatePillar(
            "employmentStability",
            "employerStability",
            e.target.value
          )
        }
      />

      <label>sectorCyclicality:</label>
      <input
        type="number"
        value={PILLARS.employmentStability.sectorCyclicality}
        onChange={(e) =>
          updatePillar(
            "employmentStability",
            "sectorCyclicality",
            e.target.value
          )
        }
      />

      <h6>financialBehaviour</h6>
      <label>financialSkor:</label>
      <input
        type="number"
        value={PILLARS.financialBehaviour.financialSkor}
        onChange={(e) =>
          updatePillar("financialBehaviour", "financialSkor", e.target.value)
        }
      />

      <label>bankBehaviour:</label>
      <input
        type="number"
        value={PILLARS.financialBehaviour.bankBehaviour}
        onChange={(e) =>
          updatePillar("financialBehaviour", "bankBehaviour", e.target.value)
        }
      />

      <label>rentBehaviour:</label>
      <input
        type="number"
        value={PILLARS.financialBehaviour.rentBehaviour}
        onChange={(e) =>
          updatePillar("financialBehaviour", "rentBehaviour", e.target.value)
        }
      />

      <h6>creditDefaultRisk</h6>
      <label>defaultHistory:</label>
      <input
        type="number"
        value={PILLARS.creditDefaultRisk.defaultHistory}
        onChange={(e) =>
          updatePillar("creditDefaultRisk", "defaultHistory", e.target.value)
        }
      />

      <label>insuranceSkor:</label>
      <input
        type="number"
        value={PILLARS.creditDefaultRisk.insuranceSkor}
        onChange={(e) =>
          updatePillar("creditDefaultRisk", "insuranceSkor", e.target.value)
        }
      />

      <h6>documentationStrength</h6>
      <label>documentation:</label>
      <input
        type="number"
        value={PILLARS.documentationStrength.documentation}
        onChange={(e) =>
          updatePillar("documentationStrength", "documentation", e.target.value)
        }
      />

      <label>forensics:</label>
      <input
        type="number"
        value={PILLARS.documentationStrength.forensics}
        onChange={(e) =>
          updatePillar("documentationStrength", "forensics", e.target.value)
        }
      />

      <hr />
      <h3>Policy Weights</h3>

      <label>incomeAffordability:</label>
      <input
        type="number"
        value={POLICY_WEIGHTS.incomeAffordability}
        onChange={(e) =>
          updatePolicyWeight("incomeAffordability", e.target.value)
        }
      />

      <label>employmentStability:</label>
      <input
        type="number"
        value={POLICY_WEIGHTS.employmentStability}
        onChange={(e) =>
          updatePolicyWeight("employmentStability", e.target.value)
        }
      />

      <label>financialBehaviour:</label>
      <input
        type="number"
        value={POLICY_WEIGHTS.financialBehaviour}
        onChange={(e) =>
          updatePolicyWeight("financialBehaviour", e.target.value)
        }
      />

      <label>creditDefaultRisk:</label>
      <input
        type="number"
        value={POLICY_WEIGHTS.creditDefaultRisk}
        onChange={(e) =>
          updatePolicyWeight("creditDefaultRisk", e.target.value)
        }
      />

      <label>documentationStrength:</label>
      <input
        type="number"
        value={POLICY_WEIGHTS.documentationStrength}
        onChange={(e) =>
          updatePolicyWeight("documentationStrength", e.target.value)
        }
      />

      <hr />
      <button onClick={() => setResult(calculateFinalDecision(input))}>
        Run Scoring
      </button>
      {result && (
        <>
          <h3>Result</h3>
          <p>{JSON.stringify(result, null, 2)}</p>
        </>
      )}
    </div>
  );
}
